<template>
  <section name="theme-config" class="theme-config border-t border-secondary-20" v-aha-emit-action.view>
    <section
      class="preview-section
    bg-primary-50 flex flex-col
    justify-center items-center border-r border-secondary-20"
    >
      <ThemePreview class="w-10/12" :theme="themePreview"></ThemePreview>
    </section>
    <section class="config-section">
      <section class="p-4 config-field">
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $i18nPresenter.t('Theme name') }}</span>
            <aha-input
              v-model="theme.themeName"
              :maxLength="50"
            />
          </div>
          <div class="divider bg-base-30 h-px"></div>
          <div class="flex flex-col gap-2">
            <div class="flex justify-between items-center">
              <span class="flex font-bold">
                {{ $i18nPresenter.t('Logo') }}
              </span>
              <span v-if="!logoThumbnail">
                <aha-antd-button
                  variant="secondary"
                  :class="'flex items-center'"
                  @click="onClickAddLogo"
                >
                  <aha-icon
                    name="plus-new"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    class="mr-2"
                  />
                  {{ $i18nPresenter.t('Add') }}
                </aha-antd-button>
              </span>
            </div>
            <aha-background-image
              v-if="logoThumbnail"
              :thumbnailBackgroundImage="logoThumbnail"
              :deleteImage="removeLogo"
              :changeImage="onClickAddLogo"
              :textChangeImage="$i18nPresenter.t('Change image')"
              :textEditImage="$i18nPresenter.t('Edit image')"
            />
          </div>
          <div class="divider bg-base-30 h-px"></div>
          <div class="flex flex-col gap-2">
            <div class="flex justify-between items-center">
              <span class="flex font-bold">
                {{ $i18nPresenter.t('Background image') }}
              </span>
              <span v-if="!thumbnail">
                <aha-antd-button
                  variant="secondary"
                  :class="'flex items-center'"
                  @click="onClickAddBackgroundImage"
                >
                  <aha-icon
                    name="plus-new"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    class="mr-2"
                  /> {{ $i18nPresenter.t('Add') }}
                </aha-antd-button>
              </span>
            </div>
            <aha-background-image
              v-if="thumbnail"
              :thumbnailBackgroundImage="thumbnail"
              :deleteImage="removeBackgroundImage"
              :changeImage="onClickAddBackgroundImage"
              :textChangeImage="$i18nPresenter.t('Change image')"
              :textEditImage="$i18nPresenter.t('Edit image')"
            />
          </div>
          <div class="flex flex-col gap-2" v-if="thumbnail">
            <div class="font-bold">
              {{ $i18nPresenter.t('Transparency') }}
            </div>
            <aha-antd-slider-with-input
              :min="0"
              :max="100"
              :value="theme.visibility"
              :change="(visibility) => { theme.visibility = visibility }"
              suffix="%"
            />
          </div>
          <div class="divider bg-base-30 h-px"></div>
          <div class="flex gap-2 justify-between items-center">
            <span class="font-bold">{{ $i18nPresenter.t('Background colour') }} </span>
            <ColorPickerWrap :color="theme.baseColour" @change="(color) => theme.baseColour = color" />
          </div>
          <div class="divider bg-base-30 h-px"></div>
          <div class="flex gap-2 justify-between items-center">
            <span class="font-bold">{{ $i18nPresenter.t('Text') }}
            </span>
            <div class="flex items-center gap-2">
              <SelectFontWrap
                :fontFamily="theme.fontFamily"
                @setFontFamily="(fontFamily) => theme.fontFamily = fontFamily"
                :checkPlan="false"
              />
              <ColorButton
                :color="'#ffffff'"
                :active="isTextColourLight"
                @color-selected="(color) => theme.textColour = color"
              />
              <ColorButton
                :color="'#313131'"
                :active="!isTextColourLight"
                @color-selected="(color) => theme.textColour = color"
              />
            </div>
          </div>
          <AhaInfoBox
            variant="error"
            v-if="!isContrastEnough"
          >
            {{ $i18nPresenter.t('For readability, make sure there is a ' +
              'clear contrast between text colour and base colour.') }}
          </AhaInfoBox>
          <div class="divider bg-base-30 h-px"></div>
          <div class="flex flex-col justify-between gap-2">
            <span class="flex gap-1.5 font-bold">
              {{ $i18nPresenter.t('Visualisation colours') }}
              <span
                name="theme-color-palette"
                class="text-base-55"
              >
                <i class="fas fa-question-circle " />
              </span>
              <aha-toggle-tip
                to="theme-color-palette"
                :text="visualisationTooltipContent"
              >
              </aha-toggle-tip>
            </span>
            <aha-antd-select-multi-color
              class="h-10"
              :options="presetColorPalettes"
              :value="theme.colorPalette?.id"
              :change="handleSelectColorPalette"
            />
          </div>
        </div>
      </section>
      <section class="flex p-4 border-t border-secondary-20">
        <aha-antd-button
          name="save-theme"
          v-aha-emit-action
          :submitting="isCreating"
          class="flex-1"
          variant="primary"
          size="lg"
          @click="saveTheme"
        >
          {{ $i18nPresenter.t('Save') }}
        </aha-antd-button>
      </section>
    </section>
    <UploadImageV2
      v-if="isShowImagePickerModal"
      type="themeConfig"
      @hideModal="isShowImagePickerModal = false"
      @callback="(imageUrl) => {
        handlePickImage(imageUrl);
      }"
      @selectExistImage="(obj) => {
        handlePickImage(obj?.origin);
      }"
      :updateGifInBackground="gifHandler"
    />
    <component
      :is="cropModalComponent"
      v-if="isShowCropModal && imageUrlCrop"
      :aspect-ratio="currentRatio"
      :image-url="imageUrlCrop"
      @on-cancel="() => isShowCropModal = false"
      @hideModal="() => isShowCropModal = false"
      :updateInBackground="cropHandler"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImageV2 from '@/components/presentations/CropImage.v2.vue'
import CropImageV1 from '@/components/presentations/CropImage.adapter.vue'
import Constants from '@/constant'

import ColorPickerWrap from '@/components-v2/presentation-editor/side-right/right-nav/ColorPickerWrap.vue'
import SelectFontWrap from '@/components-v2/presentation-editor/side-right/right-nav/SelectFontWrap.vue'
import ColorButton from '@/components-v2/presentation-editor/side-right/right-nav/ColorButton.vue'
import ThemePreview from '@/components-v2/presentation-editor/side-right/right-nav/ThemePreview.vue'
import { getFileNameFromURL } from '@/util'
import { isContrast, isColorLight } from '@/utils/color.utils'

export default {
  name: 'ThemeConfig',
  components: {
    ColorPickerWrap,
    SelectFontWrap,
    UploadImageV2,
    CropImageV2,
    CropImageV1,
    ThemePreview,
    ColorButton,
  },
  props: {
    themeName: {
      type: String,
      default: 'My new theme',
    },
    logo: {
      type: String,
      default: null,
    },
    baseColour: {
      type: String,
      default: '#ffffff',
    },
    backgroundImage: {
      type: String,
      default: null,
    },
    visibility: {
      type: Number,
      default: 100,
    },
    fontFamily: {
      type: String,
      default: 'Nunito',
    },
    textColour: {
      type: String,
      default: '#313131',
    },
    colorPalette: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      theme: {
        themeName: this.themeName,
        logo: this.logo,
        baseColour: this.baseColour,
        backgroundImage: this.backgroundImage,
        visibility: this.visibility,
        fontFamily: this.fontFamily,
        textColour: this.textColour,
        colorPalette: this.colorPalette,
      },
      thumbnail: this.backgroundImage?.large || null,
      logoThumbnail: this.logo?.large || null,
      // For handle images uploading and cropping
      cropModalComponent: 'CropImageV2',
      cropHandler: null,
      gifHandler: null,
      isShowImagePickerModal: false,
      isShowCropModal: false,
      DEFAULT_IMAGE_RATIOS: Constants.DEFAULT_IMAGE_RATIOS,
      currentRatio: Constants.DEFAULT_IMAGE_RATIOS?.BACKGROUND_IMAGE,
      imageUrlCrop: null,
    }
  },
  computed: {
    ...mapGetters('presetModule', ['presetColorPalettes']),
    ...mapGetters('themeModule', ['isCreating']),
    isTextColourLight() {
      return isColorLight(this.theme.textColour)
    },
    visualisationTooltipContent() {
      return this.$i18nPresenter.t('Visualisation colours apply to all charts in quiz, poll, and scale slides, and to audience response displays in word cloud, open ended, and brainstorm slides')
    },
    isContrastEnough() {
      return isContrast(this.theme.textColour, this.theme.baseColour)
    },
    themePreview() {
      return {
        ...this.theme,
        backgroundImage: { large: this.thumbnail },
        logo: { thumbnail: this.logoThumbnail },
        colorPalette: this.theme?.colorPalette ? this.theme?.colorPalette : this.presetColorPalettes?.[0],
      }
    },
  },
  destroyed() {
    // revoke all obj url
    if (this.thumbnail) URL.revokeObjectURL(this.thumbnail)
    if (this.logoThumbnail) URL.revokeObjectURL(this.logoThumbnail)
  },
  methods: {
    isGif(url) {
      const fileName = getFileNameFromURL(url)
      return fileName.endsWith('.gif')
    },
    async fetchGif(imageUrl) {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const fileName = getFileNameFromURL(imageUrl)
        const file = new File([blob], fileName, {
          type: blob.type,
        })
        return { file, blob }
      } catch (error) {
        return null
      }
    },
    removeBackgroundImage() {
      URL.revokeObjectURL(this.thumbnail)
      this.theme.backgroundImage = null
      this.thumbnail = null
    },
    removeLogo() {
      URL.revokeObjectURL(this.logoThumbnail)
      this.theme.logo = null
      this.logoThumbnail = null
    },
    async onClickAddLogo() {
      this.isShowImagePickerModal = true
      this.cropHandler = this.handleCropLogo
      this.cropModalComponent = 'CropImageV1'
      this.gifHandler = this.handleGifLogo
      this.currentRatio = this.DEFAULT_IMAGE_RATIOS.LOGO
    },
    async onClickAddBackgroundImage() {
      this.isShowImagePickerModal = true
      this.cropHandler = this.handlerCropBackgroundImage
      this.cropModalComponent = 'CropImageV2'
      this.gifHandler = this.handleGifBackgroundImage
      this.currentRatio = this.DEFAULT_IMAGE_RATIOS.BACKGROUND_IMAGE
    },
    async handlePickImage(imageUrl) {
      this.isShowImagePickerModal = false
      if (this.isGif(imageUrl)) {
        this.gifHandler({ imageUrl })
        return
      }

      this.imageUrlCrop = imageUrl
      this.isShowCropModal = true
    },
    async handleGifLogo(data) {
      this.isShowImagePickerModal = false
      const { imageUrl } = data
      const { file, blob } = await this.fetchGif(imageUrl)
      this.theme.logo = file
      this.logoThumbnail = URL.createObjectURL(blob)
    },
    async handleGifBackgroundImage(data) {
      this.isShowImagePickerModal = false
      const { imageUrl } = data
      const { file, blob } = await this.fetchGif(imageUrl)
      this.theme.backgroundImage = file
      this.thumbnail = URL.createObjectURL(blob)
    },
    async handleCropLogo(data) {
      this.isShowCropModal = false
      this.theme.logo = data?.file
      this.logoThumbnail = URL.createObjectURL(data?.file)
    },
    async handlerCropBackgroundImage(data) {
      this.isShowCropModal = false
      this.theme.backgroundImage = data?.file
      this.thumbnail = URL.createObjectURL(data?.file)
    },
    saveTheme() {
      this.$emit('saveTheme', this.theme)
    },
    handleSelectColorPalette(colorPaletteId) {
      const colorPalette = this.presetColorPalettes.find((palette) => palette.id === colorPaletteId)
      this.theme.colorPalette = colorPalette
    },
  },
}
</script>

<style lang="scss" scoped>
.theme-config {
  display: grid;
  grid-template-columns: 1fr 384px;
  & .config-field {
    --height: 573px;
    @apply overflow-auto;

    height: var(--height);
    max-height: var(--height);
  }
}

input[type="color"] {
  @apply w-6 h-6 rounded-full border-secondary-20 border-2;
  appearance: none;
  border-style: solid;
  -webkit-appearance: none;
  overflow: hidden;
  padding: 0;
  cursor: pointer;
}

/* Remove default styling in WebKit browsers */
input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

span:has(> input[type="color"]) {
  @apply border-secondary-20 p-2 rounded-lg;
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
}
</style>
